// Sblocco biometrico come gate LOCALE dell'app installata (ARCHITECTURE §4):
// WebAuthn platform authenticator. Non sostituisce l'auth server.

function b64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function webauthnSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

/** Vero se il device ha un authenticator biometrico integrato (Face ID/Touch ID/Hello). */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Etichetta biometrica adattiva al device. */
export function biometricLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'Face ID / Touch ID';
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Windows/.test(ua)) return 'Windows Hello';
  if (/Android/.test(ua)) return 'Sblocco biometrico';
  return 'Sblocco biometrico';
}

interface StoredCred {
  id: string; // credential rawId base64url
}

/** Registra una credenziale biometrica; ritorna il JSON da persistere (webauthn_cred_json). */
export async function registerBiometric(userId: string, userName: string): Promise<string> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: 'Archivio Documentale', id: location.hostname },
      user: { id: new TextEncoder().encode(userId), name: userName, displayName: userName },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required' },
      timeout: 60000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Registrazione biometrica annullata');
  const stored: StoredCred = { id: b64url(cred.rawId) };
  return JSON.stringify(stored);
}

/** Sblocca verificando la presenza biometrica. Ritorna true se l'utente è verificato. */
export async function verifyBiometric(credJson: string): Promise<boolean> {
  let stored: StoredCred;
  try {
    stored = JSON.parse(credJson);
  } catch {
    return false;
  }
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ type: 'public-key', id: fromB64url(stored.id) as BufferSource }],
        userVerification: 'required',
        timeout: 60000,
        rpId: location.hostname,
      },
    });
    return !!assertion;
  } catch {
    return false;
  }
}
